import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
    Plus, ArrowLeft, Loader2, X, Search, AlertCircle,
    Truck, HardHat, Boxes, DollarSign, Play, Check, Ban, Pencil, Eye, Share2,
    Clock, CheckCircle2, AlertTriangle,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useForm } from "../components/forms/useForm";
import { Field, TextInput, NumberInput, DateInput, SelectInput, Textarea } from "../components/forms/Field";
import { required } from "../lib/validation";
import { Toast, ConfirmDialog } from "./master/assets";
import AuthedImage, { PhotoLightbox } from "../components/AuthedImage";
import PublicLinkModal from "../components/PublicLinkModal";
import EmptyState from "../components/EmptyState";
import { Briefcase } from "lucide-react";
import dict from "../i18n/es-MX";

const fmtMXN = (n) => n == null ? "—" : new Intl.NumberFormat("es-MX",{style:"currency",currency:"MXN",maximumFractionDigits:0}).format(n);
import { fmtDate } from "../lib/dateFormat";

const STATUS_COLORS = {
    programado: "bg-slate-700/40 text-slate-300 border-slate-600",
    en_progreso: "bg-brand-orange/20 text-brand-orange border-brand-orange/40",
    completado: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    cancelado: "bg-red-500/15 text-red-300 border-red-500/40",
};
const PAY_COLORS = {
    pagado: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    parcial: "bg-brand-orange/20 text-brand-orange border-brand-orange/40",
    pendiente: "bg-red-500/15 text-red-300 border-red-500/40",
};

const JOB_STATUSES = ["programado", "en_progreso", "completado", "cancelado"];

function StatusBadge({ s }) {
    return <span data-testid={`status-${s}`} className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider border ${STATUS_COLORS[s]||""}`}>{dict.jobs.statusLabel[s] || s}</span>;
}
function PayBadge({ p }) {
    return <span data-testid={`payment-${p}`} className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider border ${PAY_COLORS[p]||""}`}>{dict.jobs.paymentLabel[p] || p}</span>;
}
function ClassBadge({ c }) {
    const styleMap = {
        externo: "bg-sky-500/15 text-sky-300 border-sky-500/40",
        interno: "bg-slate-700/40 text-slate-300 border-slate-600",
        renta: "bg-violet-500/15 text-violet-300 border-violet-500/40",
    };
    return <span data-testid={`class-${c}`} className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider border ${styleMap[c]||styleMap.interno}`}>{dict.jobs.classLabel[c] || c}</span>;
}
function Chip({ children, to, testid }) {
    const cls = "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-slate-800/70 border border-slate-700 text-slate-200 hover:border-brand-orange hover:text-brand-orange transition";
    if (to) return <Link to={to} data-testid={testid} className={cls}>{children}</Link>;
    return <span data-testid={testid} className={cls}>{children}</span>;
}

// ============================================================
// useMasters — fetch clients/assets/operators/materials in one go
// ============================================================

function useMasters() {
    const [m, setM] = useState({ clients: [], assets: [], operators: [], materials: [], loading: true, error: "" });
    useEffect(() => {
        (async () => {
            try {
                const [c, a, o, mt] = await Promise.all([
                    api.get("/clients"), api.get("/assets"), api.get("/operators"), api.get("/materials"),
                ]);
                setM({ clients: c.data, assets: a.data, operators: o.data, materials: mt.data, loading: false, error: "" });
            } catch (e) { setM(s => ({ ...s, loading: false, error: formatApiError(e) })); }
        })();
    }, []);
    return m;
}

// ============================================================
// LIST
// ============================================================

export function JobsList() {
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]); const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [filters, setFilters] = useState({ status: "", client_id: "", asset_id: "", operator_id: "", q: "" });
    const m = useMasters();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            const r = await api.get("/jobs", { params });
            setJobs(r.data);
        } catch (e) { setToast({ msg: formatApiError(e), type: "error" }); }
        finally { setLoading(false); }
    }, [filters]);
    useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

    const clientById = useMemo(() => Object.fromEntries(m.clients.map(c => [c.id, c])), [m.clients]);
    const assetById = useMemo(() => Object.fromEntries(m.assets.map(a => [a.id, a])), [m.assets]);
    const opById = useMemo(() => Object.fromEntries(m.operators.map(o => [o.id, o])), [m.operators]);

    return <div data-testid="jobs-page" className="space-y-5">
        <header className="flex items-start justify-between flex-wrap gap-3">
            <div>
                <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">{dict.jobs.title}</h1>
                <p className="mt-2 text-slate-400 text-sm">{dict.jobs.subtitle}</p>
            </div>
            <button onClick={()=>navigate("/trabajos/nuevo")} data-testid="new-job-button" className="inline-flex items-center gap-2 h-11 px-4 rounded-lg bg-brand-orange hover:bg-orange-400 text-slate-950 font-semibold uppercase tracking-wider text-sm transition"><Plus size={18}/>{dict.jobs.newJob}</button>
        </header>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-end">
            <div className="relative flex-1 min-w-[180px] max-w-md">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"/>
                <input value={filters.q} onChange={e=>{const v=e.target.value; setFilters(f=>({...f, q: v}));}} placeholder={dict.common.search} data-testid="filter-q" className="w-full h-10 pl-9 pr-3 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
            </div>
            <select value={filters.status} onChange={e=>{const v=e.target.value; setFilters(f=>({...f,status:v}));}} data-testid="filter-status" className="h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm">
                <option value="">{dict.common.all}</option>
                {JOB_STATUSES.map(k => <option key={k} value={k}>{dict.jobs.statusLabel[k]}</option>)}
            </select>
            <select value={filters.client_id} onChange={e=>{const v=e.target.value; setFilters(f=>({...f,client_id:v}));}} data-testid="filter-client" className="h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm">
                <option value="">{dict.jobs.cols.client}</option>
                {m.clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={filters.asset_id} onChange={e=>{const v=e.target.value; setFilters(f=>({...f,asset_id:v}));}} data-testid="filter-asset" className="h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm">
                <option value="">{dict.jobs.cols.assets}</option>
                {m.assets.map(a=><option key={a.id} value={a.id}>{a.nickname}</option>)}
            </select>
            <select value={filters.operator_id} onChange={e=>{const v=e.target.value; setFilters(f=>({...f,operator_id:v}));}} data-testid="filter-operator" className="h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm">
                <option value="">{dict.jobs.cols.operators}</option>
                {m.operators.map(o=><option key={o.id} value={o.id}>{o.full_name}</option>)}
            </select>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="jobs-table">
                <thead className="bg-slate-900/80 text-xs uppercase text-slate-400">
                    <tr>
                        <th className="text-left px-4 py-3">{dict.jobs.cols.code}</th>
                        <th className="text-left px-4 py-3">{dict.jobs.cols.client}</th>
                        <th className="text-left px-4 py-3">{dict.jobs.cols.start}</th>
                        <th className="text-left px-4 py-3">{dict.jobs.cols.end}</th>
                        <th className="text-left px-4 py-3">{dict.jobs.cols.assets}</th>
                        <th className="text-left px-4 py-3">{dict.jobs.cols.operators}</th>
                        <th className="text-left px-4 py-3">{dict.jobs.cols.classification}</th>
                        <th className="text-right px-4 py-3">{dict.jobs.cols.revenue}</th>
                        <th className="text-left px-4 py-3">{dict.jobs.cols.status}</th>
                        <th className="text-left px-4 py-3">{dict.jobs.cols.payment}</th>
                        <th className="text-right px-4 py-3">{dict.common.actions}</th>
                    </tr>
                </thead>
                <tbody>
                    {loading && <tr><td colSpan={11} className="px-4 py-10 text-center"><Loader2 className="animate-spin inline"/></td></tr>}
                    {!loading && jobs.length===0 && <tr><td colSpan={11} className="px-0 py-0"><EmptyState icon={Briefcase} title={dict.jobs.empty} ctaLabel={dict.jobs.newJob} ctaHref="/trabajos/nuevo" testid="jobs-empty"/></td></tr>}
                    {!loading && jobs.map(j => <tr key={j.id} data-testid={`job-row-${j.id}`} className="border-t border-slate-800 hover:bg-slate-900/60">
                        <td className="px-4 py-3 font-mono text-xs"><Link to={`/trabajos/${j.id}`} className="text-slate-100 hover:text-brand-orange">{j.code}</Link></td>
                        <td className="px-4 py-3 text-slate-300">{j.client_name || clientById[j.client_id]?.name || "—"}</td>
                        <td className="px-4 py-3 text-slate-400">{fmtDate(j.start_date)}</td>
                        <td className="px-4 py-3 text-slate-400" data-testid={`end-date-${j.id}`}>{j.end_date ? fmtDate(j.end_date) : "—"}</td>
                        <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                                {j.asset_ids.slice(0,3).map((id, i) => <Chip key={id}>{j.asset_nicknames?.[i] || assetById[id]?.nickname || id.slice(0,6)}</Chip>)}
                                {j.asset_ids.length>3 && <Chip>+{j.asset_ids.length-3}</Chip>}
                            </div>
                        </td>
                        <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                                {j.operator_ids.slice(0,2).map((id, i) => <Chip key={id}>{(j.operator_full_names?.[i] || opById[id]?.full_name || id.slice(0,6)).split(" ")[0]}</Chip>)}
                                {j.operator_ids.length>2 && <Chip>+{j.operator_ids.length-2}</Chip>}
                            </div>
                        </td>
                        <td className="px-4 py-3"><ClassBadge c={j.classification}/></td>
                        <td className="px-4 py-3 text-right text-slate-200">{fmtMXN(j.revenue_mxn)}</td>
                        <td className="px-4 py-3"><StatusBadge s={j.status}/></td>
                        <td className="px-4 py-3"><PayBadge p={j.payment_status}/></td>
                        <td className="px-4 py-3"><div className="flex justify-end gap-1">
                            <Link to={`/trabajos/${j.id}`} data-testid={`view-job-${j.id}`} className="p-2 rounded hover:bg-slate-800" title={dict.jobs.actions.view}><Eye size={16}/></Link>
                            {!["completado","cancelado"].includes(j.status) && <Link to={`/trabajos/${j.id}/editar`} data-testid={`edit-job-${j.id}`} className="p-2 rounded hover:bg-slate-800" title={dict.common.edit}><Pencil size={16}/></Link>}
                        </div></td>
                    </tr>)}
                </tbody>
            </table>
        </div>
        {toast && <Toast {...toast} onClose={()=>setToast(null)}/>}
    </div>;
}

// ============================================================
// FORM
// ============================================================

const blankExtPay = (op_id, op, mode = "por_dia") => {
    const r = (op && op.default_external_rates) || {};
    const common = {
        operator_id: op_id,
        commission_pct: r.commission_pct || 0,
        bonus_fixed_mxn: r.bonus_fixed_mxn || 0,
    };
    if (mode === "por_viaje") {
        return {
            ...common,
            base_per_trip_mxn: r.base_per_trip_mxn || 0,
            minimo_viajes: r.minimo_viajes ?? 0,
            base_per_day_mxn: null, days: null,
            viajes_realizados: 0,
        };
    }
    return {
        ...common,
        base_per_day_mxn: r.base_per_day_mxn || 0,
        days: 0,
        base_per_trip_mxn: null, minimo_viajes: null,
    };
};

export function JobForm() {
    const navigate = useNavigate(); const { id } = useParams(); const isEdit = !!id;
    const m = useMasters();
    const [loading, setLoading] = useState(isEdit);
    const [toast, setToast] = useState(null);

    const form = useForm({
        initial: {
            client_id: "", description: "", location_text: "", start_date: "", end_date: "",
            classification: "interno", external_pay_mode: "por_dia",
            asset_ids: [], operator_ids: [],
            material_assignments: [], external_pay: [],
            revenue_facturado_mxn: "", revenue_efectivo_mxn: "",
            payment_method: "efectivo", payment_status: "pendiente",
            amount_paid_mxn: "", initial_status: "programado",
            // Phase 8D — Renta
            rental_mode: "mensual",
            rental_period_count: "",
            rental_amount_per_period_mxn: "",
            rental_start_date: "",
            rental_auto_renew: false,
            rental_grace_days: 3,
        },
        validators: {
            client_id: required, description: required, location_text: required,
            start_date: required, payment_method: required, payment_status: required,
        },
    });

    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                const r = await api.get(`/jobs/${id}`);
                const j = r.data;
                form.reset({
                    client_id: j.client_id, description: j.description, location_text: j.location_text,
                    start_date: j.start_date.slice(0,10), end_date: j.end_date ? j.end_date.slice(0,10) : "",
                    classification: j.classification, asset_ids: j.asset_ids, operator_ids: j.operator_ids,
                    material_assignments: j.material_assignments,
                    external_pay: j.external_pay.map(p => ({
                        operator_id: p.operator_id,
                        base_per_day_mxn: p.base_per_day_mxn,
                        commission_pct: p.commission_pct, bonus_fixed_mxn: p.bonus_fixed_mxn, days: p.days,
                        base_per_trip_mxn: p.base_per_trip_mxn ?? null,
                        minimo_viajes: p.minimo_viajes ?? null,
                        viajes_realizados: p.viajes_realizados ?? 0,
                    })),
                    external_pay_mode: j.external_pay_mode || "por_dia",
                    revenue_facturado_mxn: j.revenue_facturado_mxn ?? j.revenue_mxn,
                    revenue_efectivo_mxn: j.revenue_efectivo_mxn ?? 0,
                    payment_method: j.payment_method,
                    payment_status: j.payment_status, amount_paid_mxn: j.amount_paid_mxn || "",
                    initial_status: j.status,
                    // Phase 8D — Renta (loaded from server)
                    rental_mode: j.rental_mode || "mensual",
                    rental_period_count: j.rental_period_count ?? "",
                    rental_amount_per_period_mxn: j.rental_amount_per_period_mxn ?? "",
                    rental_start_date: j.rental_start_date ? j.rental_start_date.slice(0,10) : "",
                    rental_auto_renew: !!j.rental_auto_renew,
                    rental_grace_days: j.rental_grace_days ?? 3,
                });
            } catch (e) { setToast({ msg: formatApiError(e), type: "error" }); }
            finally { setLoading(false); }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isEdit]);

    const v = form.values;
    const facturadoNum = parseFloat(v.revenue_facturado_mxn) || 0;
    const efectivoNum = parseFloat(v.revenue_efectivo_mxn) || 0;
    const revenueNum = facturadoNum + efectivoNum;

    // Sync external_pay when operator selection OR pay mode changes (only externo)
    useEffect(() => {
        if (v.classification !== "externo") {
            if (v.external_pay.length > 0) form.set("external_pay", []);
            return;
        }
        const mode = v.external_pay_mode || "por_dia";
        const map = Object.fromEntries(v.external_pay.map(p => [p.operator_id, p]));
        const next = v.operator_ids.map(opid => {
            const existing = map[opid];
            if (!existing) return blankExtPay(opid, opById[opid], mode);
            // Mode mismatch — keep operator + comm/bonus, reset mode-specific fields
            const fieldsForMode = mode === "por_viaje"
                ? { base_per_trip_mxn: existing.base_per_trip_mxn ?? 0, minimo_viajes: existing.minimo_viajes ?? 0, base_per_day_mxn: null, days: null }
                : { base_per_day_mxn: existing.base_per_day_mxn ?? 0, days: existing.days ?? 0, base_per_trip_mxn: null, minimo_viajes: null };
            return {
                operator_id: opid,
                commission_pct: existing.commission_pct ?? 0,
                bonus_fixed_mxn: existing.bonus_fixed_mxn ?? 0,
                viajes_realizados: existing.viajes_realizados ?? 0,
                ...fieldsForMode,
            };
        });
        if (JSON.stringify(next) !== JSON.stringify(v.external_pay)) form.set("external_pay", next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [v.classification, v.external_pay_mode, v.operator_ids]);

    const opById = useMemo(() => Object.fromEntries(m.operators.map(o => [o.id, o])), [m.operators]);
    const matById = useMemo(() => Object.fromEntries(m.materials.map(mt => [mt.id, mt])), [m.materials]);

    const computePayTotal = (p) => {
        const mode = v.external_pay_mode || "por_dia";
        const comm = (parseFloat(p.commission_pct)||0) / 100 * revenueNum;
        const bonus = parseFloat(p.bonus_fixed_mxn) || 0;
        if (mode === "por_viaje") {
            const viajes = parseInt(p.viajes_realizados || 0, 10);
            const minimo = parseInt(p.minimo_viajes || 0, 10);
            const base = (parseFloat(p.base_per_trip_mxn)||0) * viajes;
            return viajes >= minimo ? base + comm + bonus : base;
        }
        const base = (parseFloat(p.base_per_day_mxn)||0) * (parseFloat(p.days)||0);
        return base + comm + bonus;
    };
    const totalExternalPay = useMemo(() =>
        v.classification === "externo" ? v.external_pay.reduce((s,p)=>s+computePayTotal(p),0) : 0,
    [v.external_pay, v.external_pay_mode, v.classification, revenueNum]); // eslint-disable-line

    const toggleAsset = (assetId) => {
        const next = v.asset_ids.includes(assetId)
            ? v.asset_ids.filter(x => x !== assetId)
            : [...v.asset_ids, assetId];
        form.set("asset_ids", next);
    };
    const toggleOp = (opid) => {
        const next = v.operator_ids.includes(opid)
            ? v.operator_ids.filter(x => x !== opid)
            : [...v.operator_ids, opid];
        form.set("operator_ids", next);
    };
    const addMaterial = () => form.set("material_assignments", [...v.material_assignments, { material_id: "", quantity: "" }]);
    const updMaterial = (i, patch) => form.set("material_assignments", v.material_assignments.map((m,j) => j===i?{...m,...patch}:m));
    const rmMaterial = (i) => form.set("material_assignments", v.material_assignments.filter((_,j) => j!==i));
    const updPay = (opid, patch) => form.set("external_pay", v.external_pay.map(p => p.operator_id===opid?{...p,...patch}:p));

    const onSubmit = (e) => {
        e.preventDefault();
        form.handleSubmit(async (vals) => {
            const isRenta = vals.classification === "renta";

            // Renta has different rules — handle separately
            if (isRenta) {
                if (vals.asset_ids.length === 0) {
                    setToast({msg: dict.jobs.info.assignAtLeastOne, type:"error"}); return;
                }
                if (!vals.rental_mode) {
                    setToast({msg: dict.jobs.fields.rentalMode + " requerido", type:"error"}); return;
                }
                const pc = parseInt(vals.rental_period_count, 10);
                if (!pc || pc < 1) {
                    setToast({msg: dict.jobs.fields.rentalPeriodCount + " debe ser mayor a 0", type:"error"}); return;
                }
                const amt = parseFloat(vals.rental_amount_per_period_mxn);
                if (!amt || amt <= 0) {
                    setToast({msg: dict.jobs.fields.rentalAmountPerPeriod + " debe ser mayor a 0", type:"error"}); return;
                }
                const body = {
                    client_id: vals.client_id,
                    description: vals.description.trim(),
                    location_text: vals.location_text.trim(),
                    start_date: vals.start_date,
                    end_date: vals.end_date || null,
                    classification: "renta",
                    asset_ids: vals.asset_ids,
                    operator_ids: [],
                    material_assignments: [],
                    external_pay: [],
                    external_pay_mode: "por_dia",
                    rental_mode: vals.rental_mode,
                    rental_period_count: pc,
                    rental_amount_per_period_mxn: amt,
                    rental_start_date: vals.rental_start_date || vals.start_date,
                    rental_auto_renew: !!vals.rental_auto_renew,
                    rental_grace_days: parseInt(vals.rental_grace_days || 3, 10),
                    payment_method: vals.payment_method,
                    payment_status: vals.payment_status,
                    amount_paid_mxn: null,
                };
                if (!isEdit) body.initial_status = vals.initial_status;
                try {
                    if (isEdit) await api.patch(`/jobs/${id}`, body);
                    else await api.post("/jobs", body);
                    setToast({msg: dict.common.saved, type:"success"});
                    setTimeout(()=>navigate("/trabajos"), 600);
                } catch (err) { setToast({msg: formatApiError(err) || dict.common.saveError, type:"error"}); }
                return;
            }

            // Cross-field validation
            if (vals.asset_ids.length === 0 && vals.operator_ids.length === 0) {
                setToast({msg: dict.jobs.info.assignAtLeastOne, type:"error"}); return;
            }
            if (vals.end_date && vals.start_date && vals.end_date < vals.start_date) {
                setToast({msg: dict.jobs.info.startBeforeEnd, type:"error"}); return;
            }
            // Materials
            const matIds = vals.material_assignments.map(x=>x.material_id);
            if (new Set(matIds).size !== matIds.length) {
                setToast({msg: dict.jobs.info.duplicateMaterial, type:"error"}); return;
            }
            for (const m of vals.material_assignments) {
                if (!m.material_id || !m.quantity || parseFloat(m.quantity) <= 0) {
                    setToast({msg: dict.jobs.info.quantityPositive, type:"error"}); return;
                }
            }
            // External pay completeness
            if (vals.classification === "externo") {
                const payOps = new Set(vals.external_pay.map(p=>p.operator_id));
                if (vals.operator_ids.some(o => !payOps.has(o))) {
                    setToast({msg: dict.jobs.info.externalPayMissing, type:"error"}); return;
                }
            }
            // Revenue split validation: at least one of facturado/efectivo must be > 0
            const facturado = parseFloat(vals.revenue_facturado_mxn) || 0;
            const efectivo = parseFloat(vals.revenue_efectivo_mxn) || 0;
            const rev = facturado + efectivo;
            if (rev <= 0) {
                setToast({ msg: dict.jobs.info.revenueRequired || "Debes registrar al menos un monto de ingreso.", type: "error" });
                return;
            }
            if (vals.payment_status === "parcial") {
                const ap = parseFloat(vals.amount_paid_mxn);
                if (!ap || ap <= 0 || ap >= rev) {
                    setToast({msg: dict.jobs.info.amountPaidPositive, type:"error"}); return;
                }
            }

            const body = {
                client_id: vals.client_id, description: vals.description.trim(), location_text: vals.location_text.trim(),
                start_date: vals.start_date, end_date: vals.end_date || null,
                classification: vals.classification, asset_ids: vals.asset_ids, operator_ids: vals.operator_ids,
                material_assignments: vals.material_assignments.map(m => ({ material_id: m.material_id, quantity: parseFloat(m.quantity) })),
                external_pay: vals.classification === "externo" ? vals.external_pay.map(p => {
                    const mode = vals.external_pay_mode || "por_dia";
                    if (mode === "por_viaje") {
                        return {
                            operator_id: p.operator_id,
                            base_per_trip_mxn: parseFloat(p.base_per_trip_mxn)||0,
                            minimo_viajes: parseInt(p.minimo_viajes || 0, 10),
                            commission_pct: parseFloat(p.commission_pct)||0,
                            bonus_fixed_mxn: parseFloat(p.bonus_fixed_mxn)||0,
                        };
                    }
                    return {
                        operator_id: p.operator_id,
                        base_per_day_mxn: parseFloat(p.base_per_day_mxn)||0,
                        commission_pct: parseFloat(p.commission_pct)||0,
                        bonus_fixed_mxn: parseFloat(p.bonus_fixed_mxn)||0,
                        days: parseFloat(p.days)||0,
                    };
                }) : [],
                external_pay_mode: vals.classification === "externo" ? (vals.external_pay_mode || "por_dia") : "por_dia",
                revenue_mxn: rev,
                revenue_facturado_mxn: facturado,
                revenue_efectivo_mxn: efectivo,
                payment_method: vals.payment_method, payment_status: vals.payment_status,
                amount_paid_mxn: vals.payment_status === "parcial" ? parseFloat(vals.amount_paid_mxn) : null,
            };
            if (!isEdit) body.initial_status = vals.initial_status;

            try {
                if (isEdit) await api.patch(`/jobs/${id}`, body);
                else await api.post("/jobs", body);
                setToast({msg: dict.common.saved, type:"success"});
                setTimeout(()=>navigate("/trabajos"), 600);
            } catch (err) { setToast({msg: formatApiError(err) || dict.common.saveError, type:"error"}); }
        });
    };

    if (m.loading || loading) return <div className="p-10 text-center"><Loader2 className="animate-spin inline"/></div>;

    return <div className="space-y-5 max-w-4xl">
        <Link to="/trabajos" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100"><ArrowLeft size={14}/>{dict.jobs.title}</Link>
        <h1 className="font-display text-4xl font-extrabold tracking-tight">{isEdit?dict.jobs.editJob:dict.jobs.newJob}</h1>

        <form onSubmit={onSubmit} noValidate data-testid="job-form" className="space-y-6">

            {/* 1. General */}
            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 space-y-4">
                <h2 className="font-display text-sm uppercase tracking-wider text-slate-400">{dict.jobs.sections.general}</h2>
                <Field label={dict.jobs.fields.client} required error={form.errors.client_id}>
                    <SelectInput {...form.field("client_id")} error={form.errors.client_id} options={[{value:"",label:"—"},...m.clients.filter(c=>!c.archived).map(c=>({value:c.id,label:c.name}))]} data-testid="job-client"/>
                </Field>
                <Field label={dict.jobs.fields.description} required error={form.errors.description}>
                    <Textarea {...form.field("description")} error={form.errors.description} rows={2} data-testid="job-description"/>
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label={dict.jobs.fields.location} required error={form.errors.location_text}>
                        <TextInput {...form.field("location_text")} error={form.errors.location_text} data-testid="job-location"/>
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label={dict.jobs.fields.startDate} required error={form.errors.start_date}>
                            <DateInput {...form.field("start_date")} error={form.errors.start_date} data-testid="job-start"/>
                        </Field>
                        <Field label={dict.jobs.fields.endDate} error={form.errors.end_date}>
                            <DateInput {...form.field("end_date")} data-testid="job-end"/>
                        </Field>
                    </div>
                </div>
            </section>

            {/* 2. Clasificación */}
            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 space-y-4">
                <h2 className="font-display text-sm uppercase tracking-wider text-slate-400">{dict.jobs.sections.classification}</h2>
                <div className="flex gap-2">
                    {["interno","externo","renta"].map(opt => <button type="button" key={opt} onClick={()=>!(isEdit && opt!==v.classification && (opt==="renta" || v.classification==="renta")) && form.set("classification",opt)} data-testid={`class-${opt}`}
                        disabled={isEdit && opt !== v.classification && (opt === "renta" || v.classification === "renta")}
                        className={`flex-1 h-11 px-4 rounded-lg border text-sm font-semibold uppercase tracking-wider transition ${v.classification===opt?"bg-brand-orange text-slate-950 border-brand-orange":"bg-slate-950 text-slate-300 border-slate-700 hover:border-slate-500 disabled:opacity-40 disabled:cursor-not-allowed"}`}>
                        {dict.jobs.fields[opt] || dict.jobs.classLabel[opt]}
                    </button>)}
                </div>
                {v.classification==="interno" && <div className="rounded-lg bg-slate-800/40 border border-slate-700 px-4 py-3 text-sm text-slate-300" data-testid="interno-info">{dict.jobs.info.internoCovered}</div>}
                {v.classification==="renta" && <div className="rounded-lg bg-violet-500/10 border border-violet-500/30 px-4 py-3 text-sm text-violet-200" data-testid="renta-info">{dict.jobs.info.rentaDescription}</div>}
            </section>

            {/* 2b. Términos de renta */}
            {v.classification==="renta" && (
                <section className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-5 space-y-4" data-testid="rental-terms-section">
                    <h2 className="font-display text-sm uppercase tracking-wider text-violet-300">{dict.jobs.sections.rental}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label={dict.jobs.fields.rentalMode} required>
                            <div className="flex gap-2" data-testid="rental-mode-toggle">
                                {["mensual","semanal"].map(opt => (
                                    <button type="button" key={opt} onClick={()=>form.set("rental_mode",opt)} data-testid={`rental-mode-${opt}`}
                                        className={`flex-1 h-10 px-3 rounded-lg border text-xs uppercase tracking-wider ${v.rental_mode===opt?"bg-violet-500 text-slate-950 border-violet-500 font-semibold":"bg-slate-950 text-slate-300 border-slate-700 hover:border-slate-500"}`}>
                                        {opt==="mensual" ? dict.jobs.fields.rentalModeMensual : dict.jobs.fields.rentalModeSemanal}
                                    </button>
                                ))}
                            </div>
                        </Field>
                        <Field label={dict.jobs.fields.rentalStartDate}>
                            <DateInput {...form.field("rental_start_date")} data-testid="rental-start-date"/>
                        </Field>
                        <Field label={dict.jobs.fields.rentalPeriodCount} required>
                            <NumberInput {...form.field("rental_period_count")} placeholder="6" data-testid="rental-period-count"/>
                        </Field>
                        <Field label={dict.jobs.fields.rentalAmountPerPeriod} required>
                            <NumberInput {...form.field("rental_amount_per_period_mxn")} placeholder="15000" data-testid="rental-amount"/>
                        </Field>
                        <Field label={dict.jobs.fields.rentalGraceDays}>
                            <NumberInput {...form.field("rental_grace_days")} placeholder="3" data-testid="rental-grace-days"/>
                        </Field>
                        <div className="flex items-end">
                            <label className="inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-700 bg-slate-950 cursor-pointer hover:border-slate-500 w-full" data-testid="rental-auto-renew-toggle">
                                <input type="checkbox" checked={!!v.rental_auto_renew} onChange={e=>form.set("rental_auto_renew", e.target.checked)} data-testid="rental-auto-renew" className="accent-violet-500"/>
                                <span className="text-sm">{dict.jobs.fields.rentalAutoRenew}</span>
                            </label>
                        </div>
                    </div>
                    {/* Live preview */}
                    <div className="rounded-lg bg-slate-950/60 border border-violet-500/20 p-3 grid grid-cols-3 gap-3 text-sm" data-testid="rental-preview">
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">{dict.jobs.fields.rentalAmountPerPeriod}</div>
                            <div className="font-mono font-semibold text-violet-200" data-testid="rental-preview-amount">{fmtMXN(parseFloat(v.rental_amount_per_period_mxn)||0)}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">×</div>
                            <div className="font-mono font-semibold text-slate-200" data-testid="rental-preview-periods">{parseInt(v.rental_period_count||0,10)} {v.rental_mode==="mensual"?dict.jobs.fields.rentalModeMensual.toLowerCase()+"es":"semanas"}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">{dict.jobs.fields.rentalTotalContract}</div>
                            <div className="font-mono font-bold text-brand-orange" data-testid="rental-preview-total">{fmtMXN((parseFloat(v.rental_amount_per_period_mxn)||0) * (parseInt(v.rental_period_count||0,10)))}</div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1 border-t border-violet-500/20 mt-2">
                        <Field label={dict.jobs.fields.paymentMethod} required>
                            <SelectInput {...form.field("payment_method")} options={Object.entries(dict.jobs.methodLabel).map(([k,v])=>({value:k,label:v}))} data-testid="rental-payment-method"/>
                        </Field>
                        <Field label={dict.jobs.fields.paymentStatus} required>
                            <SelectInput {...form.field("payment_status")} options={Object.entries(dict.jobs.paymentLabel).map(([k,v])=>({value:k,label:v}))} data-testid="rental-payment-status"/>
                        </Field>
                    </div>
                    <p className="text-[11px] text-violet-300/70" data-testid="rental-auto-hint">
                        {v.rental_auto_renew ? dict.jobs.info.rentalAutoRenewHint : dict.jobs.info.rentalManualHint}
                    </p>
                </section>
            )}

            {/* 3. Activos */}
            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 space-y-3">
                <h2 className="font-display text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2"><Truck size={14}/>{dict.jobs.sections.assets}</h2>
                <div className="flex flex-wrap gap-2" data-testid="asset-picker">
                    {m.assets.filter(a=>!a.archived).map(a => {
                        const sel = v.asset_ids.includes(a.id);
                        const blocked = ["en_mantenimiento","fuera_servicio"].includes(a.status);
                        return <button type="button" key={a.id} onClick={()=>!blocked && toggleAsset(a.id)} data-testid={`asset-pick-${a.id}`} disabled={blocked}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition ${sel?"bg-brand-orange text-slate-950 border-brand-orange":blocked?"bg-slate-900 text-slate-600 border-slate-800 cursor-not-allowed":"bg-slate-950 text-slate-300 border-slate-700 hover:border-slate-500"}`}>
                            {a.nickname} <span className="opacity-60">· {a.subtype||a.kind}</span>
                            {blocked && <AlertCircle size={11}/>}
                        </button>;
                    })}
                </div>
            </section>

            {/* 4. Operadores (oculto en Renta) */}
            {v.classification !== "renta" && (
            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 space-y-3">
                <h2 className="font-display text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2"><HardHat size={14}/>{dict.jobs.sections.operators}</h2>
                <div className="flex flex-wrap gap-2" data-testid="operator-picker">
                    {m.operators.filter(o=>!o.archived).map(o => {
                        const sel = v.operator_ids.includes(o.id);
                        return <button type="button" key={o.id} onClick={()=>toggleOp(o.id)} data-testid={`op-pick-${o.id}`}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition ${sel?"bg-brand-orange text-slate-950 border-brand-orange":"bg-slate-950 text-slate-300 border-slate-700 hover:border-slate-500"}`}>
                            {o.full_name}
                        </button>;
                    })}
                </div>

                {v.classification==="externo" && v.operator_ids.length>0 && (
                    <div className="mt-4 space-y-3" data-testid="external-pay-rows">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <h3 className="text-xs uppercase tracking-wider text-slate-500">{dict.jobs.sections.externalPayDetail}</h3>
                            <div className="flex gap-1" data-testid="pay-mode-toggle">
                                <span className="text-[10px] uppercase tracking-wider text-slate-500 self-center mr-1">{dict.jobs.fields.payMode}:</span>
                                {["por_dia", "por_viaje"].map(opt => (
                                    <button type="button" key={opt} onClick={() => form.set("external_pay_mode", opt)}
                                        data-testid={`pay-mode-${opt}`}
                                        className={`h-8 px-3 rounded-full text-[11px] uppercase tracking-wider border ${v.external_pay_mode===opt ? "bg-brand-orange text-slate-950 border-brand-orange font-semibold" : "bg-slate-950 text-slate-300 border-slate-700 hover:border-slate-500"}`}>
                                        {opt === "por_dia" ? dict.jobs.fields.payModePorDia : dict.jobs.fields.payModePorViaje}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {v.external_pay.map((p) => {
                            const total = computePayTotal(p);
                            const mode = v.external_pay_mode || "por_dia";
                            const viajes = parseInt(p.viajes_realizados || 0, 10);
                            const minimo = parseInt(p.minimo_viajes || 0, 10);
                            const belowMin = mode === "por_viaje" && viajes < minimo;
                            return <div key={p.operator_id} data-testid={`ext-pay-${p.operator_id}`} className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
                                <div className="text-sm font-semibold mb-2">{opById[p.operator_id]?.full_name || p.operator_id}</div>
                                {mode === "por_viaje" ? (
                                    <>
                                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
                                            <Field label={dict.jobs.fields.basePerTrip}><NumberInput value={p.base_per_trip_mxn ?? 0} onChange={ev=>updPay(p.operator_id,{base_per_trip_mxn:ev.target.value})} data-testid={`pay-trip-${p.operator_id}`}/></Field>
                                            <Field label={dict.jobs.fields.minimoViajes}><NumberInput value={p.minimo_viajes ?? 0} onChange={ev=>updPay(p.operator_id,{minimo_viajes:ev.target.value})} data-testid={`pay-min-${p.operator_id}`}/></Field>
                                            <Field label={dict.jobs.fields.commissionPct}><NumberInput value={p.commission_pct} onChange={ev=>updPay(p.operator_id,{commission_pct:ev.target.value})} data-testid={`pay-comm-${p.operator_id}`}/></Field>
                                            <Field label={dict.jobs.fields.bonus}><NumberInput value={p.bonus_fixed_mxn} onChange={ev=>updPay(p.operator_id,{bonus_fixed_mxn:ev.target.value})} data-testid={`pay-bonus-${p.operator_id}`}/></Field>
                                            <div className="text-right">
                                                <div className="text-[10px] uppercase tracking-wider text-slate-500">{dict.jobs.fields.total}</div>
                                                <div className="text-brand-orange font-semibold text-base" data-testid={`pay-total-${p.operator_id}`}>{fmtMXN(total)}</div>
                                            </div>
                                        </div>
                                        <div className="mt-2 flex items-center justify-between gap-2 text-[11px]">
                                            <span className="text-slate-400" data-testid={`pay-viajes-${p.operator_id}`}>
                                                {dict.jobs.fields.viajesRealizados}: <span className="font-mono text-slate-200">{viajes}</span>
                                                {!isEdit && <span className="ml-1 italic text-slate-500">— {dict.jobs.info.payModePorViajeHint}</span>}
                                            </span>
                                            <span className={belowMin ? "text-amber-300" : "text-emerald-300"}>
                                                {belowMin
                                                    ? dict.jobs.info.payModePorViajeBelowMin.replace("{n}", Math.max(0, minimo - viajes))
                                                    : dict.jobs.info.payModePorViajeAboveMin}
                                            </span>
                                        </div>
                                    </>
                                ) : (
                                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
                                        <Field label={dict.jobs.fields.basePerDay}><NumberInput value={p.base_per_day_mxn ?? 0} onChange={ev=>updPay(p.operator_id,{base_per_day_mxn:ev.target.value})} data-testid={`pay-base-${p.operator_id}`}/></Field>
                                        <Field label={dict.jobs.fields.days}><NumberInput value={p.days ?? 0} onChange={ev=>updPay(p.operator_id,{days:ev.target.value})} data-testid={`pay-days-${p.operator_id}`}/></Field>
                                        <Field label={dict.jobs.fields.commissionPct}><NumberInput value={p.commission_pct} onChange={ev=>updPay(p.operator_id,{commission_pct:ev.target.value})} data-testid={`pay-comm-${p.operator_id}`}/></Field>
                                        <Field label={dict.jobs.fields.bonus}><NumberInput value={p.bonus_fixed_mxn} onChange={ev=>updPay(p.operator_id,{bonus_fixed_mxn:ev.target.value})} data-testid={`pay-bonus-${p.operator_id}`}/></Field>
                                        <div className="text-right">
                                            <div className="text-[10px] uppercase tracking-wider text-slate-500">{dict.jobs.fields.total}</div>
                                            <div className="text-brand-orange font-semibold text-base" data-testid={`pay-total-${p.operator_id}`}>{fmtMXN(total)}</div>
                                        </div>
                                    </div>
                                )}
                                {!revenueNum && <div className="mt-2 text-[11px] text-slate-500">{dict.jobs.info.externoMissingRevenue}</div>}
                            </div>;
                        })}
                    </div>
                )}
            </section>
            )}

            {/* 5. Materiales (oculto en Renta) */}
            {v.classification !== "renta" && (
            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 space-y-3">
                <div className="flex items-center justify-between">
                    <h2 className="font-display text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2"><Boxes size={14}/>{dict.jobs.sections.materials}</h2>
                    <button type="button" onClick={addMaterial} data-testid="add-material" className="inline-flex items-center gap-1 text-xs px-2 h-8 rounded border border-slate-700 hover:bg-slate-800"><Plus size={12}/>{dict.jobs.info.addMaterial}</button>
                </div>
                {v.material_assignments.length===0 && <p className="text-sm text-slate-500">{dict.jobs.info.noMaterials}</p>}
                {v.material_assignments.map((mat, i) => <div key={i} data-testid={`material-row-${i}`} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-7">
                        <SelectInput value={mat.material_id} onChange={ev=>updMaterial(i,{material_id:ev.target.value})} options={[{value:"",label:"—"},...m.materials.filter(x=>!x.archived).map(x=>({value:x.id,label:`${x.name} (${dict.materials.units[x.unit]||x.unit})`}))]} data-testid={`mat-id-${i}`}/>
                    </div>
                    <div className="col-span-3">
                        <NumberInput value={mat.quantity} onChange={ev=>updMaterial(i,{quantity:ev.target.value})} placeholder={dict.jobs.fields.quantity} data-testid={`mat-qty-${i}`}/>
                    </div>
                    <div className="col-span-1 text-xs text-slate-400 pb-2">{matById[mat.material_id]?.unit ? (dict.materials.units[matById[mat.material_id].unit] || matById[mat.material_id].unit) : ""}</div>
                    <button type="button" onClick={()=>rmMaterial(i)} data-testid={`mat-rm-${i}`} className="col-span-1 h-10 rounded border border-slate-700 hover:bg-slate-800 text-slate-400 hover:text-red-400"><X size={14} className="mx-auto"/></button>
                </div>)}
            </section>
            )}

            {/* 6. Ingreso y pago (oculto en Renta — el ingreso se devenga al avanzar periodos) */}
            {v.classification !== "renta" && (
            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 space-y-4">
                <h2 className="font-display text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2"><DollarSign size={14}/>{dict.jobs.sections.revenue}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label={dict.jobs.fields.revenueFacturado || "Ingreso facturado (transferencia/crédito)"} error={form.errors.revenue_facturado_mxn}>
                        <NumberInput {...form.field("revenue_facturado_mxn")} data-testid="job-revenue-facturado" placeholder="0.00"/>
                    </Field>
                    <Field label={dict.jobs.fields.revenueEfectivo || "Ingreso en efectivo (no facturado)"} error={form.errors.revenue_efectivo_mxn}>
                        <NumberInput {...form.field("revenue_efectivo_mxn")} data-testid="job-revenue-efectivo" placeholder="0.00"/>
                    </Field>
                </div>
                <div className={`rounded-lg border px-3 py-2 text-sm flex items-center justify-between ${revenueNum <= 0 ? "border-amber-500/40 bg-amber-500/10 text-amber-200" : "border-slate-800 bg-slate-950/60 text-slate-200"}`} data-testid="job-revenue-total">
                    <span className="text-[10px] uppercase tracking-wider opacity-80">{dict.jobs.fields.totalRevenue || "Ingreso total"}</span>
                    <span className="font-mono font-semibold">{fmtMXN(revenueNum)}</span>
                </div>
                {revenueNum <= 0 && <div className="text-xs text-amber-300" data-testid="job-revenue-warning">{dict.jobs.info.revenueRequired || "Debes registrar al menos un monto de ingreso."}</div>}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label={dict.jobs.fields.paymentMethod} required>
                        <SelectInput {...form.field("payment_method")} options={Object.entries(dict.jobs.methodLabel).map(([k,v])=>({value:k,label:v}))} data-testid="job-method"/>
                    </Field>
                    <Field label={dict.jobs.fields.paymentStatus} required>
                        <SelectInput {...form.field("payment_status")} options={Object.entries(dict.jobs.paymentLabel).map(([k,v])=>({value:k,label:v}))} data-testid="job-paystatus"/>
                    </Field>
                </div>
                {v.payment_status==="parcial" && (
                    <Field label={dict.jobs.fields.amountPaid} required error={form.errors.amount_paid_mxn}>
                        <NumberInput {...form.field("amount_paid_mxn")} data-testid="job-amount-paid"/>
                    </Field>
                )}
                {/* Live summary */}
                <div className="rounded-lg bg-slate-950/60 border border-slate-800 p-3 text-sm" data-testid="job-summary">
                    <div className="grid grid-cols-3 gap-2">
                        <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{dict.jobs.info.grossRevenue}</div><div className="text-slate-100 font-semibold" data-testid="sum-revenue">{fmtMXN(revenueNum)}</div></div>
                        <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{dict.jobs.info.operatorPayout}</div><div className="text-slate-100 font-semibold" data-testid="sum-payout">{fmtMXN(totalExternalPay)}</div></div>
                        {v.classification==="externo" && <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{dict.jobs.info.estimatedMargin}</div><div className="text-emerald-300 font-semibold" data-testid="sum-margin">{fmtMXN(revenueNum-totalExternalPay)}</div></div>}
                    </div>
                    {v.classification==="externo" && <p className="mt-2 text-[10px] text-slate-500">{dict.jobs.info.marginDisclaimer}</p>}
                </div>
            </section>
            )}

            {/* 7. Initial status (only on create) */}
            {!isEdit && (
                <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
                    <h2 className="font-display text-sm uppercase tracking-wider text-slate-400 mb-3">{dict.jobs.sections.initialStatus}</h2>
                    <SelectInput {...form.field("initial_status")} options={[
                        {value:"programado",label:dict.jobs.statusLabel.programado},
                        {value:"en_progreso",label:dict.jobs.statusLabel.en_progreso},
                    ]} data-testid="job-initial-status"/>
                </section>
            )}

            <div className="flex gap-2 justify-end">
                <button type="button" onClick={()=>navigate("/trabajos")} className="h-11 px-4 rounded-lg border border-slate-700 hover:bg-slate-800 text-sm">{dict.common.cancel}</button>
                <button type="submit" disabled={form.submitting} data-testid="job-submit" className="h-11 px-5 rounded-lg bg-brand-orange hover:bg-orange-400 text-slate-950 font-semibold uppercase tracking-wider text-sm inline-flex items-center gap-2 disabled:opacity-60">
                    {form.submitting && <Loader2 className="animate-spin" size={16}/>} {form.submitting?dict.common.saving:dict.common.save}
                </button>
            </div>
        </form>

        {toast && <Toast {...toast} onClose={()=>setToast(null)}/>}
    </div>;
}

// ============================================================
// DETAIL
// ============================================================

// ============================================================
// FieldOpsTabs — admin read-only view of phase-4 captured data
// ============================================================

function FieldOpsTabs({ jobId, job, assetById, opById, matById }) {
    const TABS = ["photos", "readings", "fuel", "viajes"];
    const [tab, setTab] = useState("photos");
    const [photos, setPhotos] = useState([]);
    const [readings, setReadings] = useState([]);
    const [fuel, setFuel] = useState([]);
    const [viajes, setViajes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lightbox, setLightbox] = useState({ id: null, caption: "" });

    useEffect(() => {
        setLoading(true);
        (async () => {
            try {
                const [p, r, f, v] = await Promise.all([
                    api.get(`/jobs/${jobId}/photos`),
                    api.get(`/meter-readings`, { params: { job_id: jobId } }),
                    api.get(`/fuel-logs`, { params: { job_id: jobId } }),
                    api.get(`/jobs/${jobId}/viajes`),
                ]);
                setPhotos(p.data || []);
                setReadings(r.data || []);
                setFuel(f.data || []);
                setViajes(v.data || []);
            } catch (e) { /* silent */ }
            finally { setLoading(false); }
        })();
    }, [jobId]);

    const nameAsset = (aid) => assetById[aid]?.nickname || aid?.slice(0, 6) || "—";
    const nameOp = (oid) => opById[oid]?.full_name || oid?.slice(0, 6) || "—";
    const nameMat = (mid) => matById[mid]?.name || mid?.slice(0, 6) || "—";

    const fmtDT = (iso) => iso
        ? new Date(iso).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
        : "—";

    const counts = { photos: photos.length, readings: readings.length, fuel: fuel.length, viajes: viajes.length };

    const Thumb = ({ photoId, caption }) => (
        <button
            type="button"
            onClick={photoId ? () => setLightbox({ id: photoId, caption }) : undefined}
            disabled={!photoId}
            className="block w-12 h-12 rounded-md overflow-hidden border border-slate-800 hover:border-brand-orange disabled:opacity-40 disabled:cursor-default"
            data-testid={photoId ? `thumb-${photoId}` : "thumb-empty"}
        >
            <AuthedImage
                photoId={photoId}
                className="w-full h-full object-cover"
                placeholderClass="w-full h-full"
            />
        </button>
    );

    return (
        <div data-testid="admin-fieldops-tabs">
            <div className="flex flex-wrap gap-2 border-b border-slate-800 mb-3">
                {TABS.map((k) => (
                    <button
                        key={k} type="button"
                        onClick={() => setTab(k)}
                        data-testid={`fieldtab-${k}`}
                        className={`-mb-px px-3 h-9 text-xs font-semibold uppercase tracking-wider transition border-b-2 ${tab === k ? "border-brand-orange text-brand-orange" : "border-transparent text-slate-400 hover:text-slate-100"}`}
                    >
                        {dict.jobs.fieldTabs[k]} <span className="ml-1 font-mono text-[10px] text-slate-500">{counts[k]}</span>
                    </button>
                ))}
            </div>

            {loading && <div className="text-center py-6"><Loader2 className="animate-spin inline text-slate-500"/></div>}

            {!loading && tab === "photos" && (
                <div data-testid="fieldtab-photos-content">
                    {photos.length === 0 ? (
                        <div className="text-sm text-slate-500 py-4">{dict.jobs.fieldTabs.empty}</div>
                    ) : (
                        <table className="min-w-full text-sm">
                            <thead className="text-[11px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="text-left py-2 w-16"></th>
                                    <th className="text-left py-2">{dict.jobs.fieldTabs.asset}</th>
                                    <th className="text-left py-2">{dict.jobs.fieldTabs.moment}</th>
                                    <th className="text-left py-2">{dict.jobs.fieldTabs.checklistKey}</th>
                                    <th className="text-left py-2">{dict.jobs.fieldTabs.operator}</th>
                                    <th className="text-left py-2">{dict.jobs.fieldTabs.takenAt}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {photos.map((p) => (
                                    <tr key={p.id} className="border-t border-slate-800" data-testid={`photo-row-${p.id}`}>
                                        <td className="py-2"><Thumb photoId={p.photo_id} caption={`${nameAsset(p.asset_id)} • ${dict.jobs.fieldTabs[p.moment] || p.moment} • ${p.checklist_key}`}/></td>
                                        <td className="py-2">{nameAsset(p.asset_id)}</td>
                                        <td className="py-2"><span className="text-xs uppercase tracking-wider text-slate-400">{dict.jobs.fieldTabs[p.moment] || p.moment}</span></td>
                                        <td className="py-2 font-mono text-xs">{p.checklist_key}</td>
                                        <td className="py-2">{nameOp(p.operator_id)}</td>
                                        <td className="py-2 text-slate-400">{fmtDT(p.taken_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {!loading && tab === "readings" && (
                <div data-testid="fieldtab-readings-content">
                    {readings.length === 0 ? (
                        <div className="text-sm text-slate-500 py-4">{dict.jobs.fieldTabs.empty}</div>
                    ) : (
                        <table className="min-w-full text-sm">
                            <thead className="text-[11px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="text-left py-2 w-16"></th>
                                    <th className="text-left py-2">{dict.jobs.fieldTabs.asset}</th>
                                    <th className="text-left py-2">{dict.jobs.fieldTabs.kind}</th>
                                    <th className="text-right py-2">{dict.jobs.fieldTabs.value}</th>
                                    <th className="text-left py-2">{dict.jobs.fieldTabs.takenAt}</th>
                                    <th className="text-left py-2">{dict.jobs.fieldTabs.operator}</th>
                                    <th className="text-left py-2">{dict.jobs.fieldTabs.statusCol}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {readings.map((r) => {
                                    let badgeCls = "bg-slate-700/40 text-slate-300 border-slate-500/40";
                                    let badgeLabel = dict.verify.pendingBadge;
                                    let Icon = Clock;
                                    if (r.verified) { badgeCls = "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"; badgeLabel = dict.verify.verifiedBadge; Icon = CheckCircle2; }
                                    else if (r.flagged) { badgeCls = "bg-orange-500/15 text-orange-300 border-orange-500/40"; badgeLabel = dict.verify.flaggedBadge; Icon = AlertTriangle; }
                                    return (
                                        <tr key={r.id} className="border-t border-slate-800" data-testid={`reading-row-${r.id}`}>
                                            <td className="py-2"><Thumb photoId={r.photo_id} caption={`${nameAsset(r.asset_id)} • ${r.value}`}/></td>
                                            <td className="py-2">{nameAsset(r.asset_id)}</td>
                                            <td className="py-2 text-xs uppercase tracking-wider text-slate-400">{r.kind === "hour_meter_horas" ? "Horas" : "Km"}</td>
                                            <td className="py-2 text-right font-mono">{r.value}</td>
                                            <td className="py-2 text-slate-400">{fmtDT(r.taken_at)}</td>
                                            <td className="py-2">{nameOp(r.operator_id)}</td>
                                            <td className="py-2">
                                                <span data-testid={`reading-badge-${r.id}`}
                                                    className={`px-2 h-5 text-[10px] rounded-full border inline-flex items-center gap-1 uppercase tracking-wider ${badgeCls}`}>
                                                    <Icon size={10}/> {badgeLabel}
                                                </span>
                                                {r.flagged && r.flag_reason && (
                                                    <div className="text-[11px] text-orange-300 mt-1" data-testid={`reading-flag-reason-${r.id}`}>{r.flag_reason}</div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {!loading && tab === "fuel" && (
                <div data-testid="fieldtab-fuel-content">
                    {fuel.length === 0 ? (
                        <div className="text-sm text-slate-500 py-4">{dict.jobs.fieldTabs.empty}</div>
                    ) : (
                        <table className="min-w-full text-sm">
                            <thead className="text-[11px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="text-left py-2 w-16"></th>
                                    <th className="text-left py-2">{dict.jobs.fieldTabs.asset}</th>
                                    <th className="text-left py-2">{dict.jobs.fieldTabs.takenAt}</th>
                                    <th className="text-right py-2">{dict.jobs.fieldTabs.liters}</th>
                                    <th className="text-right py-2">{dict.jobs.fieldTabs.cost}</th>
                                    <th className="text-right py-2">{dict.jobs.fieldTabs.lph}</th>
                                    <th className="text-left py-2">{dict.jobs.fieldTabs.anomaly}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {fuel.map((f) => (
                                    <tr key={f.id} className="border-t border-slate-800" data-testid={`fuel-row-${f.id}`}>
                                        <td className="py-2"><Thumb photoId={f.photo_id} caption={`${nameAsset(f.asset_id)} • ${f.liters} L`}/></td>
                                        <td className="py-2">{nameAsset(f.asset_id)}</td>
                                        <td className="py-2 text-slate-400">{f.date}</td>
                                        <td className="py-2 text-right font-mono">{f.liters}</td>
                                        <td className="py-2 text-right font-mono">{fmtMXN(f.total_cost_mxn)}</td>
                                        <td className="py-2 text-right font-mono">{f.liters_per_hour ?? f.kml ?? "—"}</td>
                                        <td className="py-2">{f.anomaly_flag ? <span className="text-red-300 text-xs">{f.anomaly_reason}</span> : <span className="text-slate-500">—</span>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {!loading && tab === "viajes" && (
                <div data-testid="fieldtab-viajes-content">
                    {viajes.length === 0 ? (
                        <div className="text-sm text-slate-500 py-4">{dict.jobs.fieldTabs.empty}</div>
                    ) : (
                        <table className="min-w-full text-sm">
                            <thead className="text-[11px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="text-left py-2 w-16"></th>
                                    <th className="text-right py-2">{dict.jobs.fieldTabs.viajeNumber}</th>
                                    <th className="text-left py-2">{dict.jobs.fieldTabs.asset}</th>
                                    <th className="text-left py-2">{dict.jobs.fieldTabs.origin}</th>
                                    <th className="text-left py-2">{dict.jobs.fieldTabs.destination}</th>
                                    <th className="text-left py-2">{dict.jobs.fieldTabs.material}</th>
                                    <th className="text-right py-2">{dict.jobs.fieldTabs.quantity}</th>
                                    <th className="text-left py-2">{dict.jobs.fieldTabs.takenAt}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {viajes.map((v) => (
                                    <tr key={v.id} className="border-t border-slate-800" data-testid={`viaje-row-${v.id}`}>
                                        <td className="py-2"><Thumb photoId={(v.photo_ids || [])[0]} caption={`Viaje #${v.viaje_number} • ${nameAsset(v.asset_id)}`}/></td>
                                        <td className="py-2 text-right font-mono">#{v.viaje_number}</td>
                                        <td className="py-2">{nameAsset(v.asset_id)}</td>
                                        <td className="py-2 text-slate-300">{v.origin_text || "—"}</td>
                                        <td className="py-2 text-slate-300">{v.destination_text || "—"}</td>
                                        <td className="py-2">{nameMat(v.material_id)}</td>
                                        <td className="py-2 text-right font-mono">{v.quantity}</td>
                                        <td className="py-2 text-slate-400">{fmtDT(v.taken_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            <PhotoLightbox
                photoId={lightbox.id}
                caption={lightbox.caption}
                onClose={() => setLightbox({ id: null, caption: "" })}
            />
        </div>
    );
}

export function JobDetail() {
    const { id } = useParams(); const navigate = useNavigate();
    const [job, setJob] = useState(null); const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [transitioning, setTransitioning] = useState(null); // {to_status}
    const [transitionNote, setTransitionNote] = useState("");
    const [shareOpen, setShareOpen] = useState(false);
    const m = useMasters();

    const load = useCallback(async () => {
        setLoading(true);
        try { const r = await api.get(`/jobs/${id}`); setJob(r.data); }
        catch (e) { setToast({msg:formatApiError(e),type:"error"}); }
        finally { setLoading(false); }
    }, [id]);
    useEffect(() => { load(); }, [load]);

    const clientName = useMemo(() => m.clients.find(c=>c.id===job?.client_id)?.name, [m.clients, job]);
    const assetById = useMemo(() => Object.fromEntries(m.assets.map(a=>[a.id,a])), [m.assets]);
    const opById = useMemo(() => Object.fromEntries(m.operators.map(o=>[o.id,o])), [m.operators]);
    const matById = useMemo(() => Object.fromEntries(m.materials.map(mt=>[mt.id,mt])), [m.materials]);

    const doTransition = async () => {
        if (!transitioning) return;
        if (transitioning.to_status === "cancelado" && !transitionNote.trim()) {
            setToast({msg: dict.jobs.info.cancelReasonRequired, type:"error"}); return;
        }
        try {
            await api.post(`/jobs/${id}/transition`, {
                to_status: transitioning.to_status,
                note: transitionNote.trim() || null,
            });
            setTransitioning(null); setTransitionNote("");
            setToast({msg: dict.common.saved, type:"success"});
            load();
        } catch (e) { setToast({msg: formatApiError(e), type:"error"}); }
    };

    const [advancing, setAdvancing] = useState(false);
    const advancePeriod = async () => {
        if (!window.confirm(dict.jobs.info.rentalAdvanceConfirm)) return;
        setAdvancing(true);
        try {
            await api.post(`/jobs/${id}/rental/advance-period`);
            setToast({msg: dict.jobs.info.rentalAdvanceSuccess, type:"success"});
            load();
        } catch (e) { setToast({msg: formatApiError(e), type:"error"}); }
        finally { setAdvancing(false); }
    };

    if (loading || m.loading) return <div className="p-10 text-center"><Loader2 className="animate-spin inline"/></div>;
    if (!job) return <div className="p-10 text-center text-slate-500">{dict.common.loadError}</div>;

    const canEdit = !["completado","cancelado"].includes(job.status);

    return <div className="space-y-5 max-w-5xl">
        <Link to="/trabajos" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100"><ArrowLeft size={14}/>{dict.jobs.title}</Link>
        <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
                <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="font-display text-4xl font-extrabold tracking-tight font-mono">{job.code}</h1>
                    <StatusBadge s={job.status}/>
                    <ClassBadge c={job.classification}/>
                    <PayBadge p={job.payment_status}/>
                </div>
                <p className="mt-2 text-slate-400">{clientName} · {fmtDate(job.start_date)} {job.end_date && `→ ${fmtDate(job.end_date)}`}</p>
                <p className="mt-1 text-slate-300">{job.location_text}</p>
                <p className="mt-3 text-slate-200">{job.description}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
                <button onClick={()=>setShareOpen(true)} data-testid="public-link-button" className="inline-flex items-center gap-1 h-10 px-3 rounded-lg border border-brand-orange/60 bg-brand-orange/10 hover:bg-brand-orange/20 text-brand-orange text-sm font-semibold transition"><Share2 size={14}/>{dict.publicLink.share}</button>
                {canEdit && <Link to={`/trabajos/${id}/editar`} data-testid="edit-job-btn" className="inline-flex items-center gap-1 h-10 px-3 rounded-lg border border-slate-700 hover:bg-slate-800 text-sm"><Pencil size={14}/>{dict.common.edit}</Link>}
                {job.status==="programado" && <button onClick={()=>setTransitioning({to_status:"en_progreso"})} data-testid="start-job-btn" className="inline-flex items-center gap-1 h-10 px-3 rounded-lg bg-brand-orange text-slate-950 font-semibold text-sm"><Play size={14}/>{dict.jobs.actions.start}</button>}
                {job.status==="en_progreso" && <button onClick={()=>setTransitioning({to_status:"completado"})} data-testid="complete-job-btn" className="inline-flex items-center gap-1 h-10 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm"><Check size={14}/>{dict.jobs.actions.complete}</button>}
                {canEdit && <button onClick={()=>setTransitioning({to_status:"cancelado"})} data-testid="cancel-job-btn" className="inline-flex items-center gap-1 h-10 px-3 rounded-lg bg-red-700/60 hover:bg-red-700 text-white text-sm"><Ban size={14}/>{dict.jobs.actions.cancel}</button>}
            </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                <h2 className="text-xs uppercase tracking-wider text-slate-400 mb-2">{dict.jobs.sections.assets}</h2>
                <div className="flex flex-wrap gap-2">{job.asset_ids.map(aid => <Chip key={aid} to={`/activos/${aid}`} testid={`detail-asset-${aid}`}>{assetById[aid]?.nickname||aid.slice(0,6)}</Chip>)}{job.asset_ids.length===0 && <span className="text-sm text-slate-500">—</span>}</div>
            </section>
            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                <h2 className="text-xs uppercase tracking-wider text-slate-400 mb-2">{dict.jobs.sections.operators}</h2>
                <div className="flex flex-wrap gap-2">{job.operator_ids.map(oid => <Chip key={oid} to={`/operadores/${oid}`} testid={`detail-op-${oid}`}>{opById[oid]?.full_name||oid.slice(0,6)}</Chip>)}{job.operator_ids.length===0 && <span className="text-sm text-slate-500">—</span>}</div>
            </section>
        </div>

        {job.material_assignments?.length>0 && (
            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                <h2 className="text-xs uppercase tracking-wider text-slate-400 mb-2">{dict.jobs.sections.materials}</h2>
                <ul className="text-sm space-y-1">{job.material_assignments.map((mt,i) => {
                    const name = job.material_names?.[i] || matById[mt.material_id]?.name || mt.material_id;
                    const unitKey = job.material_units?.[i] || matById[mt.material_id]?.unit;
                    const unitLabel = unitKey ? (dict.materials.units[unitKey] || unitKey) : "";
                    return <li key={i}>{name} — <span className="text-brand-orange">{mt.quantity}</span> {unitLabel}</li>;
                })}</ul>
            </section>
        )}

        <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
            <h2 className="text-xs uppercase tracking-wider text-slate-400 mb-2">{dict.jobs.sections.revenue}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{dict.jobs.fields.revenue}</div><div className="text-slate-100 font-semibold" data-testid="detail-revenue">{fmtMXN(job.revenue_mxn)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{dict.jobs.fields.paymentMethod}</div><div>{dict.jobs.methodLabel[job.payment_method]}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{dict.jobs.fields.paymentStatus}</div><div><PayBadge p={job.payment_status}/></div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{dict.jobs.fields.amountPaid}</div><div>{fmtMXN(job.amount_paid_mxn||0)}</div></div>
            </div>
        </section>

        {job.classification==="renta" && (
            <section className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-4" data-testid="rental-card">
                <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                    <h2 className="text-xs uppercase tracking-wider text-violet-300">{dict.jobs.sections.rental}</h2>
                    {job.status === "en_progreso" && (job.rental_periods_completed || 0) < (job.rental_period_count || 0) ? (
                        <button onClick={advancePeriod} disabled={advancing} data-testid="rental-advance-btn"
                            className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-violet-500 hover:bg-violet-400 text-slate-950 font-semibold text-xs uppercase tracking-wider disabled:opacity-60">
                            {advancing ? <Loader2 className="animate-spin" size={14}/> : <Play size={14}/>}
                            {dict.jobs.info.rentalAdvancePeriod}
                        </button>
                    ) : (job.rental_periods_completed || 0) >= (job.rental_period_count || 0) ? (
                        <span className="text-[11px] text-emerald-300 uppercase tracking-wider" data-testid="rental-completed-badge">{dict.jobs.info.rentalAllPeriodsDone}</span>
                    ) : null}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{dict.jobs.fields.rentalMode}</div><div className="font-semibold" data-testid="rental-mode">{job.rental_mode === "mensual" ? dict.jobs.fields.rentalModeMensual : dict.jobs.fields.rentalModeSemanal}</div></div>
                    <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{dict.jobs.fields.rentalPeriodsCompleted}</div><div className="font-mono font-semibold" data-testid="rental-periods">{job.rental_periods_completed||0} / {job.rental_period_count||0}</div></div>
                    <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{dict.jobs.fields.rentalAmountPerPeriod}</div><div className="font-mono font-semibold" data-testid="rental-amount">{fmtMXN(job.rental_amount_per_period_mxn||0)}</div></div>
                    <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{dict.jobs.fields.rentalCurrentPeriodEnd}</div><div className="font-mono font-semibold" data-testid="rental-period-end">{fmtDate(job.rental_current_period_end)}</div></div>
                    <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{dict.jobs.fields.rentalAutoRenew}</div><div className="font-semibold" data-testid="rental-auto">{job.rental_auto_renew ? "Sí" : "No"}</div></div>
                    <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{dict.jobs.fields.rentalAccruedRevenue}</div><div className="font-mono font-semibold text-brand-orange" data-testid="rental-accrued">{fmtMXN(job.revenue_mxn||0)}</div></div>
                    <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{dict.jobs.fields.rentalTotalContract}</div><div className="font-mono font-semibold text-slate-200" data-testid="rental-total-contract">{fmtMXN((job.rental_amount_per_period_mxn||0)*(job.rental_period_count||0))}</div></div>
                    <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{dict.jobs.fields.rentalGraceDays}</div><div className="font-mono font-semibold">{job.rental_grace_days||0}</div></div>
                </div>
            </section>
        )}

        {job.classification==="externo" && job.external_pay.length>0 && (
            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h2 className="text-xs uppercase tracking-wider text-slate-400">{dict.jobs.sections.externalPayDetail}</h2>
                    <span data-testid="detail-pay-mode" className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-slate-800 border border-slate-700 text-slate-200">
                        {dict.jobs.fields.payMode}: {job.external_pay_mode === "por_viaje" ? dict.jobs.fields.payModePorViaje : dict.jobs.fields.payModePorDia}
                    </span>
                </div>
                {job.external_pay_mode === "por_viaje" ? (
                    <table className="min-w-full text-sm" data-testid="detail-payout-table">
                        <thead className="text-[11px] uppercase tracking-wider text-slate-500"><tr>
                            <th className="text-left py-2">{dict.jobs.fields.operators}</th>
                            <th className="text-right py-2">{dict.jobs.fields.basePerTrip}</th>
                            <th className="text-right py-2">{dict.jobs.fields.minimoViajes}</th>
                            <th className="text-right py-2">{dict.jobs.fields.viajesRealizados}</th>
                            <th className="text-right py-2">{dict.jobs.fields.commissionPct}</th>
                            <th className="text-right py-2">{dict.jobs.fields.bonus}</th>
                            <th className="text-right py-2">{dict.jobs.fields.total}</th>
                        </tr></thead>
                        <tbody>{job.external_pay.map(p => {
                            const v = p.viajes_realizados ?? 0;
                            const mn = p.minimo_viajes ?? 0;
                            const active = v >= mn;
                            return (<tr key={p.operator_id} className="border-t border-slate-800" data-testid={`detail-pay-row-${p.operator_id}`}>
                                <td className="py-2">{opById[p.operator_id]?.full_name||p.operator_id.slice(0,6)}</td>
                                <td className="py-2 text-right">{fmtMXN(p.base_per_trip_mxn)}</td>
                                <td className="py-2 text-right">{mn}</td>
                                <td className="py-2 text-right font-mono">{v}</td>
                                <td className={`py-2 text-right ${active ? "text-emerald-300" : "text-slate-500 line-through"}`}>{p.commission_pct}%</td>
                                <td className={`py-2 text-right ${active ? "text-emerald-300" : "text-slate-500 line-through"}`}>{fmtMXN(p.bonus_fixed_mxn)}</td>
                                <td className="py-2 text-right text-brand-orange font-semibold">{fmtMXN(p.computed_total_mxn)}</td>
                            </tr>);
                        })}
                        <tr className="border-t border-slate-700"><td colSpan={6} className="py-2 text-right font-semibold">{dict.jobs.fields.total}</td><td className="py-2 text-right text-brand-orange font-bold" data-testid="detail-payout-total">{fmtMXN(job.external_pay_total_mxn)}</td></tr></tbody>
                    </table>
                ) : (
                    <table className="min-w-full text-sm" data-testid="detail-payout-table">
                        <thead className="text-[11px] uppercase tracking-wider text-slate-500"><tr>
                            <th className="text-left py-2">{dict.jobs.fields.operators}</th>
                            <th className="text-right py-2">{dict.jobs.fields.basePerDay}</th>
                            <th className="text-right py-2">{dict.jobs.fields.days}</th>
                            <th className="text-right py-2">{dict.jobs.fields.commissionPct}</th>
                            <th className="text-right py-2">{dict.jobs.fields.bonus}</th>
                            <th className="text-right py-2">{dict.jobs.fields.total}</th>
                        </tr></thead>
                        <tbody>{job.external_pay.map(p => <tr key={p.operator_id} className="border-t border-slate-800">
                            <td className="py-2">{opById[p.operator_id]?.full_name||p.operator_id.slice(0,6)}</td>
                            <td className="py-2 text-right">{fmtMXN(p.base_per_day_mxn)}</td>
                            <td className="py-2 text-right">{p.days}</td>
                            <td className="py-2 text-right">{p.commission_pct}%</td>
                            <td className="py-2 text-right">{fmtMXN(p.bonus_fixed_mxn)}</td>
                            <td className="py-2 text-right text-brand-orange font-semibold">{fmtMXN(p.computed_total_mxn)}</td>
                        </tr>)}<tr className="border-t border-slate-700"><td colSpan={5} className="py-2 text-right font-semibold">{dict.jobs.fields.total}</td><td className="py-2 text-right text-brand-orange font-bold" data-testid="detail-payout-total">{fmtMXN(job.external_pay_total_mxn)}</td></tr></tbody>
                    </table>
                )}
            </section>
        )}

        <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
            <h2 className="text-xs uppercase tracking-wider text-slate-400 mb-3">{dict.jobs.fieldTabs.title}</h2>
            <FieldOpsTabs jobId={id} job={job} assetById={assetById} opById={opById} matById={matById}/>
        </section>

        {/* Transition modal */}
        {transitioning && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="transition-modal">
            <div className="absolute inset-0 bg-black/70" onClick={()=>{setTransitioning(null);setTransitionNote("");}}/>
            <div className="relative w-full max-w-md rounded-2xl bg-slate-900 border border-slate-800 p-5 space-y-4">
                <h3 className="font-display text-lg font-bold">{dict.jobs.statusLabel[transitioning.to_status]}</h3>
                <Field label={transitioning.to_status==="cancelado"?dict.jobs.fields.cancelReason:dict.jobs.fields.note}>
                    <Textarea value={transitionNote} onChange={e=>setTransitionNote(e.target.value)} rows={3} data-testid="transition-note"/>
                </Field>
                <div className="flex justify-end gap-2">
                    <button onClick={()=>{setTransitioning(null);setTransitionNote("");}} className="h-10 px-4 rounded border border-slate-700 text-sm">{dict.common.cancel}</button>
                    <button onClick={doTransition} data-testid="transition-confirm" className="h-10 px-4 rounded bg-brand-orange text-slate-950 font-semibold text-sm">{dict.common.confirm}</button>
                </div>
            </div>
        </div>}

        {toast && <Toast {...toast} onClose={()=>setToast(null)}/>}

        <PublicLinkModal
            open={shareOpen}
            onClose={() => setShareOpen(false)}
            jobId={id}
            jobCode={job.code}
            clientName={clientName}
        />
    </div>;
}
